import { useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

export type UsePageAdfResult = {
	adf: DocNode | null;
	contentId: string | undefined;
};

const EMPTY_PAGE_ADF: UsePageAdfResult = { adf: null, contentId: undefined };

/** Returns renderer ADF, with the editor API as the live-page fallback. */
export function usePageAdf(contentId?: string): UsePageAdfResult {
	const { pageAdf: rendererPageAdf, pageId: rendererContentId } = useRendererActions();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const [editorPageAdf, setEditorPageAdf] = useState<UsePageAdfResult>(EMPTY_PAGE_ADF);
	const hasCurrentRendererAdf = Boolean(
		rendererPageAdf && rendererContentId && (!contentId || rendererContentId === contentId),
	);

	useEffect(() => {
		if (hasCurrentRendererAdf || !fullPageEditorPresetAPI || !contentId) {
			return;
		}

		let active = true;
		fullPageEditorPresetAPI.core.actions.requestDocument(
			(doc) => {
				if (active && doc) {
					setEditorPageAdf({ adf: doc as DocNode, contentId });
				}
			},
			{ alwaysFire: true },
		);

		return () => {
			active = false;
		};
	}, [contentId, fullPageEditorPresetAPI, hasCurrentRendererAdf]);

	if (hasCurrentRendererAdf && rendererPageAdf && rendererContentId) {
		return { adf: rendererPageAdf, contentId: rendererContentId };
	}

	return !contentId || editorPageAdf.contentId === contentId ? editorPageAdf : EMPTY_PAGE_ADF;
}
